import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

// "Samples" gift: added whenever the cart has any paid item, no threshold.
const SAMPLES_VARIANT_ID = 54146920284493;

// Tier gift (Centellian24 Madeca Cream 15ml, unlisted 0 kr product with tracked stock):
// added when the cart total reaches the threshold for the cart currency.
// Keep these values in sync with snippets/kc-cart-progress-bar.liquid.
const TIER_GIFT_VARIANT_ID = 54979522003277;
const TIER_GIFT_THRESHOLDS = {
  SEK: 69900,
  NOK: 69900,
  EUR: 6500,
  USD: 6900,
  GBP: 5500,
  DKK: 49900,
  PLN: 27900,
  CZK: 159900,
  HUF: 2599900,
  RON: 31900,
};

const GIFT_IDS = [SAMPLES_VARIANT_ID, TIER_GIFT_VARIANT_ID];

// Set when adding the tier gift fails (e.g. sold out), so we stop retrying for this page view.
let tierGiftUnavailable = false;

function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    const sectionId = el instanceof HTMLElement ? el.dataset.sectionId : null;
    if (sectionId) ids.add(sectionId);
  });
  return Array.from(ids);
}

function applySections(sections, sectionIds) {
  if (!sections) return;
  sectionIds.forEach((id) => {
    if (sections[id]) {
      morphSection(id, sections[id]);
    }
  });
}

function cartRoot() {
  const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  return root || '/';
}

function quantityOf(cart, variantId) {
  return cart.items
    .filter((item) => item.variant_id === variantId)
    .reduce((sum, item) => sum + item.quantity, 0);
}

let isProcessing = false;
let hasPendingEvent = false;

/**
 * Syncs both gift lines with the cart's current contents in a single /cart/update.js call.
 * @param {Object|null} cartSnapshot - Cart JSON from the triggering event when available;
 *   otherwise /cart.js is fetched for a fresh read.
 */
async function syncGifts(cartSnapshot) {
  if (isProcessing) {
    hasPendingEvent = true;
    return;
  }
  isProcessing = true;

  try {
    const cart =
      cartSnapshot && Array.isArray(cartSnapshot.items)
        ? cartSnapshot
        : await (await fetch(cartRoot() + 'cart.js')).json();

    const paidCount = cart.items
      .filter((item) => !GIFT_IDS.includes(item.variant_id))
      .reduce((sum, item) => sum + item.quantity, 0);

    const currency = cart.currency || 'SEK';
    const threshold = TIER_GIFT_THRESHOLDS[currency] || TIER_GIFT_THRESHOLDS.SEK;

    const wantSamples = paidCount > 0 ? 1 : 0;
    const wantTier = paidCount > 0 && cart.total_price >= threshold && !tierGiftUnavailable ? 1 : 0;

    const updates = {};
    if (quantityOf(cart, SAMPLES_VARIANT_ID) !== wantSamples) updates[SAMPLES_VARIANT_ID] = wantSamples;
    if (quantityOf(cart, TIER_GIFT_VARIANT_ID) !== wantTier) updates[TIER_GIFT_VARIANT_ID] = wantTier;

    if (Object.keys(updates).length === 0) return;

    const sectionIds = getCartSectionIds();
    const body = JSON.stringify({
      updates,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    });
    const response = await fetch(cartRoot() + 'cart/update.js', fetchConfig('json', { body }));
    const data = await response.json();

    if (!response.ok || data.errors || data.status) {
      console.error('kc-auto-gift update failed', data);
      if (updates[TIER_GIFT_VARIANT_ID] === 1) {
        // Most likely the tier gift is out of stock: stop offering it and retry the rest.
        tierGiftUnavailable = true;
        hasPendingEvent = true;
      }
      return;
    }

    applySections(data.sections, sectionIds);
  } catch (error) {
    console.error('kc-auto-gift error', error);
  } finally {
    isProcessing = false;
    if (hasPendingEvent) {
      hasPendingEvent = false;
      // No snapshot on purpose: our own update (or whatever fired meanwhile) means any
      // snapshot could be stale, so the replay always reads fresh state.
      syncGifts();
    }
  }
}

let debounceTimer = null;
let pendingSnapshot = null;

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const resource = event && event.detail && event.detail.resource;
  pendingSnapshot = resource && Array.isArray(resource.items) ? resource : null;

  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => syncGifts(pendingSnapshot), 150);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => syncGifts());
} else {
  syncGifts();
}

/* ---------------------------------------------------------------------------
   Keyboard vs touch: html.kc-kbd is set while the customer navigates with the
   keyboard, so focus rings only show for them (styles in kc-cart-slim.css).
   --------------------------------------------------------------------------- */
if (!window.__kcKbdTracking) {
  window.__kcKbdTracking = true;
  const root = document.documentElement;
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Tab' || event.key === 'Enter' || event.key === ' ' || event.key.startsWith('Arrow')) {
        root.classList.add('kc-kbd');
      }
    },
    true
  );
  document.addEventListener('pointerdown', () => root.classList.remove('kc-kbd'), true);
}

/* ---------------------------------------------------------------------------
   Quick removals. While a cart update runs, Horizon disables the whole cart
   list, so a second tap on a remove button was silently ignored. Remove buttons
   now stay tappable (kc-cart-slim.css); a tap during an update fades the row
   right away and is queued by line key, then run as soon as the previous update
   finishes, using the row's current position (line numbers shift after each
   removal, so they are looked up again at that point).
   --------------------------------------------------------------------------- */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

customElements.whenDefined('cart-items-component').then(() => {
  const ItemsClass = customElements.get('cart-items-component');
  if (!ItemsClass || ItemsClass.prototype.__kcQueuePatched) return;

  const originalRemove = ItemsClass.prototype.onLineItemRemove;
  if (typeof originalRemove !== 'function') return;

  const isBusy = (component) => component.classList.contains('cart-items-disabled');

  async function drain(component) {
    component.__kcDraining = true;
    try {
      while (component.__kcQueue && component.__kcQueue.length) {
        let waited = 0;
        while (isBusy(component) && waited < 8000) {
          await wait(40);
          waited += 40;
        }
        const key = component.__kcQueue.shift();
        const rows = (component.refs && component.refs.cartItemRows) || [];
        const index = rows.findIndex((row) => row && row.dataset && row.dataset.key === key);
        if (index >= 0) {
          originalRemove.call(component, index + 1);
          await wait(30);
        }
      }
    } finally {
      component.__kcDraining = false;
    }
  }

  ItemsClass.prototype.onLineItemRemove = function (line) {
    try {
      if (isBusy(this)) {
        const rows = (this.refs && this.refs.cartItemRows) || [];
        const row = rows[line - 1];
        const key = row && row.dataset ? row.dataset.key : null;
        if (key) {
          this.__kcQueue = this.__kcQueue || [];
          if (!this.__kcQueue.includes(key)) {
            this.__kcQueue.push(key);
            row.classList.add('kc-pending-remove');
          }
          if (!this.__kcDraining) drain(this);
          return;
        }
      }
    } catch (error) {
      // Fall through to the theme's own behaviour.
    }
    return originalRemove.call(this, line);
  };
  ItemsClass.prototype.__kcQueuePatched = true;
});

/* ---------------------------------------------------------------------------
   Cart drawer scroll lock that never moves the page.

   Horizon's dialog.js locks the page behind a dialog by making <body>
   position:fixed with top:-scrollY, then on close puts it back and scrolls to
   the saved position. That move-and-return is what customers saw as a jump.
   For the cart drawer only, we lock scrolling with overflow:hidden instead,
   which leaves the page exactly where it is:
   - While the drawer is open, html.kc-cart-open overrides dialog.js's inline
     body styles (stylesheet !important beats inline styles) and hides
     overflow on <html>/<body>. The page never moves, so on close there is
     nothing to restore and nothing to see.
   - The class is removed only on dialog:close, after dialog.js has already
     cleared its body styles, so the page never briefly becomes fixed.
   Two more browser behaviours are neutralised:
   - dialog.close() hands focus back to what was focused when it opened (the
     header cart icon) and scrolls it into view. Just before opening, focus is
     moved (without scrolling) to a tiny invisible anchor fixed in the
     viewport, so the hand-back has nothing to scroll to. Keyboard users then
     get focus back on the cart icon (without scrolling); touch users don't,
     so no focus ring is left on the icon.
   - On mobile, cart-drawer.js steps back in history on close. Scroll
     restoration is set to manual just for that moment.
   A final check puts the page back if anything else still moved it.
   Other dialogs (search, menu, quick add) are not affected.
   --------------------------------------------------------------------------- */
if (!window.__kcCartScrollFix) {
  window.__kcCartScrollFix = true;

  const LOCK_CLASS = 'kc-cart-open';
  const isCartDrawer = (target) => target instanceof Element && target.tagName === 'CART-DRAWER-COMPONENT';

  if (!document.getElementById('kc-cart-lock-style')) {
    const style = document.createElement('style');
    style.id = 'kc-cart-lock-style';
    style.textContent =
      'html.' + LOCK_CLASS + '{overflow:hidden!important;overscroll-behavior:none;scrollbar-gutter:stable;}' +
      'html.' + LOCK_CLASS + ' body{position:static!important;top:auto!important;width:auto!important;' +
      'overflow:hidden!important;overscroll-behavior:none;}';
    document.head.appendChild(style);
  }

  let anchor = null;
  const getAnchor = () => {
    if (anchor && anchor.isConnected) return anchor;
    anchor = document.createElement('span');
    anchor.tabIndex = -1;
    anchor.setAttribute('aria-hidden', 'true');
    anchor.style.cssText =
      'position:fixed;top:50%;left:0;width:1px;height:1px;opacity:0;pointer-events:none;overflow:hidden;outline:none;';
    document.body.appendChild(anchor);
    return anchor;
  };

  customElements.whenDefined('cart-drawer-component').then(() => {
    const DrawerClass = customElements.get('cart-drawer-component');
    if (!DrawerClass || DrawerClass.prototype.__kcScrollPatched) return;

    const originalShow = DrawerClass.prototype.showDialog;
    DrawerClass.prototype.showDialog = function (...args) {
      try {
        const dialog = this.refs && this.refs.dialog;
        if (!dialog || !dialog.open) {
          // Lock without moving, before dialog.js applies its body styles (it does so in the next frame).
          document.documentElement.classList.add(LOCK_CLASS);
          const previous = document.activeElement;
          this.__kcReturnFocus = previous instanceof HTMLElement && previous !== document.body ? previous : null;
          getAnchor().focus({ preventScroll: true });
        }
      } catch (error) {
        // Never block the drawer from opening.
      }
      return originalShow.apply(this, args);
    };
    DrawerClass.prototype.__kcScrollPatched = true;
  });

  let savedY = null;

  document.addEventListener(
    'dialog:open',
    (event) => {
      if (!isCartDrawer(event.target)) return;
      document.documentElement.classList.add(LOCK_CLASS);
      savedY = window.scrollY;
    },
    true
  );

  // Captured before cart-drawer.js's own close listener calls history.back().
  document.addEventListener(
    'dialog:close',
    (event) => {
      const drawer = event.target;
      if (!isCartDrawer(drawer)) return;

      const keepY = savedY;
      savedY = null;

      const hasRestoration = 'scrollRestoration' in history;
      const previous = hasRestoration ? history.scrollRestoration : null;
      if (hasRestoration) history.scrollRestoration = 'manual';

      // dialog.js has already cleared its body styles, so unlocking now cannot move the page.
      document.documentElement.classList.remove(LOCK_CLASS);

      // Keyboard users get focus back on the cart icon (without scrolling);
      // for touch users focus is simply released, so no ring is left behind.
      try {
        if (document.documentElement.classList.contains('kc-kbd')) {
          const back =
            (drawer.__kcReturnFocus && drawer.__kcReturnFocus.isConnected && drawer.__kcReturnFocus) ||
            drawer.querySelector('[aria-haspopup="dialog"]');
          if (back && typeof back.focus === 'function') back.focus({ preventScroll: true });
        } else if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      } catch (error) {
        // Focus is a nicety; ignore failures.
      }
      drawer.__kcReturnFocus = null;

      const restore = () => {
        if (keepY !== null && Math.abs(window.scrollY - keepY) > 2) {
          window.scrollTo({ top: keepY, behavior: 'instant' });
        }
      };
      const onPop = () => restore();

      restore();
      window.addEventListener('popstate', onPop);
      setTimeout(() => {
        window.removeEventListener('popstate', onPop);
        restore();
        if (hasRestoration) history.scrollRestoration = previous;
      }, 700);
    },
    true
  );
}
